import React from 'react';
import { useNavigate } from 'react-router-dom';
import { images } from '../shared/assets';
import { ContentfulImage } from '../Contentful';
import { HandDrawnButton } from '../shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../shared/CTALink/CTALink';
import { createSelfbookHandler } from '../../utils/selfbook';
import './Accommodations.css';

interface AccommodationsProps {
  property?: string;
}

const Accommodations: React.FC<AccommodationsProps> = ({ property = "patagonia" }) => {
  const navigate = useNavigate();
  const handleBooking = createSelfbookHandler(navigate, `/${property}/contact`);
  
  // Get property-specific content
  const getAccommodationsForProperty = () => {
    switch (property.toLowerCase()) {
      case 'iguazu':
        return {
          villa1: {
            title: "13 Villas",
            description: "Living area and coffee corner. Private terrace and plunge pool. Bathroom with bathtub and walk-in shower. Measuring 100 sq m / 1080 sq ft.",
            detailsPath: "/iguazu/accommodations/13-villas"
          },
          villa2: {
            title: "Master Villa",
            description: "Two bedrooms & two bathrooms. Shared living area and coffee corner. Private terrace and plunge pool. Measuring 120 sq m / 1300 sq ft.",
            detailsPath: "/iguazu/accommodations/master-villa"
          }
        };
      case 'mendoza':
        return {
          villa1: {
            title: "Vineyard Villa",
            description: "2200 sq. Feet of inside-outside living space, very private room, 250 feet away from main lodge.",
            detailsPath: "/mendoza/accommodations/vineyard-villa"
          },
          villa2: {
            title: "Two Bedroom Villa",
            description: "Idyllic for couples and families, this 3,500 square feet tranquil retreat offers a residential feel away from home.",
            detailsPath: "/mendoza/accommodations/two-bedroom-villa"
          }
        };
      case 'santa-catarina':
      case 'santacatarina':
        return {
          villa1: {
            title: "Villa Ponta dos Ganchos",
            description: "Our largest villa, with 310 square meters. Enjoy a fully equipped living space with a sauna facing the ocean, a spa bathroom, whirlpool, and private fitness center.",
            detailsPath: "/santa-catarina/accommodations/villa-ponta-dos-ganchos"
          },
          villa2: {
            title: "Villa Costa Esmeralda",
            description: "A villa spanning 230 square meters. Equipped with a dry sauna, ocean-view whirlpool, spa bathroom, fireplace, private heated pool, and comfortable outdoor seating.",
            detailsPath: "/santa-catarina/accommodations/villa-costa-esmeralda"
          }
        };
      case 'patagonia':
      default:
        return {
          villa1: {
            title: "13 Villas",
            description: "Living area with wood-burning stove and a direct view to Torres del Paine. Bathroom with bathtub and walk-in shower.",
            detailsPath: "/patagonia/accommodations/13-villas"
          },
          villa2: {
            title: "Master Villa",
            description: "Two bedrooms & two bathrooms. Shared living room with wood-burning stove and a direct view to Torres del Paine.",
            detailsPath: "/patagonia/accommodations/master-villa"
          }
        };
    }
  };
  
  const accommodations = getAccommodationsForProperty();
  
  return (
    <div className="accommodations">
      <div className="room-card">
        <ContentfulImage imageKey="villa-interior-1" fallbackSrc={images.villas13} alt="13 Villas" />
        <div className="carousel-controls">
          <button className="carousel-btn prev"></button>
          <button className="carousel-btn next"></button>
        </div>
        <h3>{accommodations.villa1.title}</h3>
        <p>{accommodations.villa1.description}</p>
        <div className="room-links">
          <CTALink 
            text="BOOK YOUR STAY" 
            type="primary" 
            color="awasi" 
            onClick={handleBooking}
            className="primary-link"
          />
          <CTALink 
            text="READ MORE" 
            type="secondary" 
            color="awasi" 
            onClick={() => navigate(accommodations.villa1.detailsPath)}
            className="secondary-link"
          />
        </div>
      </div>
      <div className="room-card">
        <ContentfulImage imageKey="villa-interior-1" fallbackSrc={images.masterVilla} alt="Master Villa" />
        <div className="carousel-controls">
          <button className="carousel-btn prev"></button>
          <button className="carousel-btn next"></button>
        </div>
        <h3>{accommodations.villa2.title}</h3>
        <p>{accommodations.villa2.description}</p>
        <div className="room-links">
          <CTALink 
            text="BOOK YOUR STAY" 
            type="primary" 
            color="awasi" 
            onClick={handleBooking}
            className="primary-link"
          />
          <CTALink 
            text="READ MORE" 
            type="secondary" 
            color="awasi" 
            onClick={() => navigate(accommodations.villa2.detailsPath)}
            className="secondary-link"
          />
        </div>
      </div>
    </div>
  );
};

export default Accommodations;
